import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@mui/material';
import { Print as PrintIcon, Close as CloseIcon } from '@mui/icons-material';

interface StockLocation {
  location_name: string;
  quantity: number;
  measurement_value: number | null;
  measurement_unit: string | null;
}

interface StockDetail {
  stock_code: string;
  rolls_per_pallet: number;
  total_sheets: number;
  total_metres: number;
  total_weight_tonnes: number;
  pallet_count: number;
  put_away_locations: StockLocation[];
}

interface ContainerData {
  container: {
    container_no: string;
    po_number: string;
    seal_no: string;
    delivery_day: string;
    delivery_time: string;
    dock: string;
    chain_of_custody: string;
    supplier_code: string;
  };
  completion: {
    seal_verified: boolean;
    condition_ok: boolean;
    water_free: boolean;
    arrival_time: string;
    departure_time: string;
    signature_url: string;
    completed_by_name: string;
  };
  photos: {
    seal_photos: string[];
    concern_photos: string[];
  };
  stock_details: StockDetail[];
}

interface DailyContainerReportProps {
  open: boolean;
  onClose: () => void;
  reportDate: string;
  containers: ContainerData[];
}

const DailyContainerReport: React.FC<DailyContainerReportProps> = ({
  open,
  onClose,
  reportDate,
  containers
}) => {
  const handlePrint = () => {
    window.print();
  };

  const formatDateTime = (timeString: string | null) => {
    if (!timeString) return '';
    const date = new Date(timeString);
    const weekday = date.toLocaleDateString('en-AU', { weekday: 'long' });
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${weekday} ${day}/${month}/${year} ${hours12}:${minutes} ${ampm}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const weekday = date.toLocaleDateString('en-AU', { weekday: 'long' });
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${weekday} ${day}/${month}/${year}`;
  };

  const formatDayOfWeek = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { weekday: 'short' });
  };

  const formatScheduledTime = (deliveryDay: string, deliveryTime: string | null) => {
    const date = new Date(deliveryDay);
    const weekday = date.toLocaleDateString('en-AU', { weekday: 'long' });
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    if (deliveryTime) {
      // Parse time string (format: "HH:MM:SS" or "HH:MM") and convert to 12hr format
      const timeParts = deliveryTime.split(':');
      const hours = parseInt(timeParts[0], 10);
      const minutes = timeParts[1];
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${weekday} ${day}/${month}/${year} ${hours12}:${minutes} ${ampm}`;
    } else {
      return `${weekday} ${day}/${month}/${year} --:-- --`;
    }
  };

  const isReel = (stockCode: string) => {
    return !stockCode.includes('X');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          '@media print': {
            maxWidth: '100%',
            margin: 0,
            boxShadow: 'none'
          }
        }
      }}
    >
      <DialogTitle sx={{ '@media print': { display: 'none' } }}>
        Daily Container Inspection Report - {formatDate(reportDate)}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ '@media print': { padding: '20mm' } }}>
          {/* Report Header (print only) */}
          <Box sx={{ display: 'none', '@media print': { display: 'block', marginBottom: 3 } }}>
            <Typography variant="h4" gutterBottom>
              Daily Container Inspection Report
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {formatDate(reportDate)}
            </Typography>
            <Typography variant="caption" display="block">
              Issued: {new Date().toLocaleString('en-AU')}
            </Typography>
          </Box>

          {/* Container Reports */}
          {containers.map((containerData, index) => {
            const { container, completion, photos, stock_details } = containerData;

            return (
              <React.Fragment key={container.container_no}>
                <Box
                  sx={{
                    mb: 4,
                    border: '2px solid #000',
                    '@media print': {
                      pageBreakInside: 'avoid',
                      pageBreakAfter: 'always',
                      marginBottom: 4
                    }
                  }}
                >
                {/* Container Header */}
                <Box
                  sx={{
                    backgroundColor: '#f5f5f5',
                    p: 1,
                    borderBottom: '2px solid #000',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {container.container_no}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {container.supplier_code || 'N/A'} (PO {container.po_number})
                    </Typography>
                  </Box>
                </Box>

                {/* Checks and Signature Section */}
                <Box sx={{ p: 1, borderBottom: '1px solid #000', display: 'flex', gap: 2 }}>
                  {/* Checks Section - Left 50% */}
                  <Box flex={1}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', textDecoration: 'underline' }} display="block">
                      Checks
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                      <Typography variant="caption" display="block">
                        <strong>Seal Checked?</strong> {completion.seal_verified ? 'Yes' : 'No'}
                      </Typography>
                      <Typography variant="caption" display="block">
                        <strong>Container Free of Water?</strong> {completion.water_free ? 'Yes' : 'No'}
                      </Typography>
                      <Typography variant="caption" display="block">
                        <strong>Pallets in good condition: Free of damage, mould, dirt, water etc.?</strong> {completion.condition_ok ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Signature Section - Right 50% */}
                  <Box flex={1} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    {completion.signature_url && (
                      <Box>
                        <img
                          src={completion.signature_url}
                          alt="Signature"
                          style={{ maxWidth: '200px', maxHeight: '60px' }}
                          onError={(e) => {
                            console.error('Failed to load signature:', completion.signature_url);
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </Box>
                    )}
                    <Typography variant="caption" sx={{ mt: 0.5 }}>Completed by: {completion.completed_by_name}</Typography>
                  </Box>
                </Box>

                {/* Times */}
                <Box sx={{ p: 1, borderBottom: '1px solid #000', display: 'flex', gap: 2 }}>
                  <Box flex={1}>
                    <Typography variant="caption" display="block"><strong>Scheduled Time:</strong> {formatScheduledTime(container.delivery_day, container.delivery_time)}</Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="caption" display="block"><strong>Arrival Time:</strong> {formatDateTime(completion.arrival_time)}</Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="caption" display="block"><strong>Departure Time:</strong> {formatDateTime(completion.departure_time)}</Typography>
                  </Box>
                </Box>

                {/* Stock Details Table */}
                <Table size="small" sx={{ '& td, & th': { border: '1px solid #000', padding: '4px 8px' } }}>
                  <TableBody>
                    {/* Section Header Row */}
                    <TableRow sx={{ backgroundColor: '#d0d0d0' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                        Packing List
                      </TableCell>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                        Physical Receipt
                      </TableCell>
                      <TableCell colSpan={1} sx={{ backgroundColor: '#e0e0e0' }}>
                      </TableCell>
                    </TableRow>

                    {/* Column Header Row */}
                    <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Stock Code</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="center">Rolls/Pallets</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }} align="right">Total Sheets/KG</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }} align="center">Location</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }} align="center">Rolls/Pallets</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }} align="right">Total Sheets/KG</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }} align="center">CoC</TableCell>
                    </TableRow>

                    {stock_details.map((stock, stockIdx) => {
                      const stockIsReel = isReel(stock.stock_code);
                      const totalQuantity = stockIsReel
                        ? stock.total_weight_tonnes * 1000
                        : stock.total_sheets;
                      const quantityDisplay = stockIsReel
                        ? `${totalQuantity.toLocaleString()} kg`
                        : `${totalQuantity.toLocaleString()} sheets`;
                      const quantityUnit = stockIsReel ? 'kg' : 'sheets';

                      // Calculate per-pallet/roll quantity for location calculations
                      const perPalletQuantity = stock.pallet_count > 0 ? totalQuantity / stock.pallet_count : 0;

                      const locations = stock.put_away_locations;
                      const firstLocation = locations[0];
                      const remainingLocations = locations.slice(1);

                      // Calculate location quantity for first location
                      const firstLocationQuantity = firstLocation
                        ? (perPalletQuantity * firstLocation.quantity).toLocaleString()
                        : '-';

                      return (
                        <React.Fragment key={`${container.container_no}-${stock.stock_code}`}>
                          {/* First row with stock details */}
                          <TableRow>
                            <TableCell rowSpan={locations.length || 1}>{stock.stock_code}</TableCell>
                            <TableCell rowSpan={locations.length || 1} align="center">{stock.pallet_count}</TableCell>
                            <TableCell rowSpan={locations.length || 1} align="right">{quantityDisplay}</TableCell>
                            {firstLocation ? (
                              <>
                                <TableCell align="center">{firstLocation.location_name}</TableCell>
                                <TableCell align="center">{firstLocation.quantity}</TableCell>
                                <TableCell align="right">{firstLocationQuantity} {quantityUnit}</TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell align="center">-</TableCell>
                                <TableCell align="center">-</TableCell>
                                <TableCell align="center">-</TableCell>
                              </>
                            )}
                            <TableCell rowSpan={locations.length || 1} align="center">
                              {container.chain_of_custody || '-'}
                            </TableCell>
                          </TableRow>

                          {/* Additional location rows */}
                          {remainingLocations.map((loc, locIdx) => {
                            const locationQuantity = (perPalletQuantity * loc.quantity).toLocaleString();
                            return (
                              <TableRow key={`${stock.stock_code}-loc-${locIdx}`}>
                                <TableCell align="center">{loc.location_name}</TableCell>
                                <TableCell align="center">{loc.quantity}</TableCell>
                                <TableCell align="right">{locationQuantity} {quantityUnit}</TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Photos Section */}
                {(photos.seal_photos.length > 0 || photos.concern_photos.length > 0) && (
                  <Box sx={{ p: 1, borderTop: '1px solid #000', display: 'flex', gap: 2 }}>
                    {/* Seal Photos - Left side (50% if concern photos exist, 100% otherwise) */}
                    {photos.seal_photos.length > 0 && (
                      <Box flex={photos.concern_photos.length > 0 ? 1 : 2}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }} display="block">
                          Seal Photos {container.seal_no ? `(${container.seal_no})` : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                          {photos.seal_photos.map((photoUrl, idx) => (
                            <img
                              key={idx}
                              src={photoUrl}
                              alt={`Seal ${idx + 1}`}
                              style={{ width: '100px', height: '100px', objectFit: 'cover', border: '1px solid #ddd' }}
                              onError={(e) => {
                                console.error('Failed to load seal photo:', photoUrl);
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Concern Photos - Right 50% */}
                    {photos.concern_photos.length > 0 && (
                      <Box flex={1}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }} display="block">Concern Photos:</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                          {photos.concern_photos.map((photoUrl, idx) => (
                            <img
                              key={idx}
                              src={photoUrl}
                              alt={`Concern ${idx + 1}`}
                              style={{ width: '100px', height: '100px', objectFit: 'cover', border: '1px solid #ddd' }}
                              onError={(e) => {
                                console.error('Failed to load concern photo:', photoUrl);
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              {/* Full Page Seal Photos */}
              {photos.seal_photos.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    '@media print': {
                      // Only add page break if there are concern photos after, or if not the last container
                      pageBreakAfter: photos.concern_photos.length > 0 || index < containers.length - 1 ? 'always' : 'auto',
                      minHeight: '100vh'
                    }
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Seal Photos - {container.container_no} {container.seal_no ? `(${container.seal_no})` : ''}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, width: '98%', height: '85vh' }}>
                    {photos.seal_photos.map((photoUrl, idx) => {
                      // Extract file path from SAS URL (remove query params and domain)
                      const filePath = photoUrl.split('?')[0].split('.blob.core.windows.net/')[1] || photoUrl;
                      return (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                              src={photoUrl}
                              alt={`Seal ${idx + 1}`}
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', border: '2px solid #000', display: 'block' }}
                              onError={(e) => {
                                console.error('Failed to load seal photo:', photoUrl);
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ mt: 1 }}>
                            Photo {idx + 1} of {photos.seal_photos.length}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                            {filePath}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Full Page Concern Photos */}
              {photos.concern_photos.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    '@media print': {
                      pageBreakAfter: index < containers.length - 1 ? 'always' : 'auto',
                      minHeight: '100vh'
                    }
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Concern Photos - {container.container_no}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, width: '98%', height: '85vh' }}>
                    {photos.concern_photos.map((photoUrl, idx) => {
                      // Extract file path from SAS URL (remove query params and domain)
                      const filePath = photoUrl.split('?')[0].split('.blob.core.windows.net/')[1] || photoUrl;
                      return (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                              src={photoUrl}
                              alt={`Concern ${idx + 1}`}
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', border: '2px solid #000', display: 'block' }}
                              onError={(e) => {
                                console.error('Failed to load concern photo:', photoUrl);
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ mt: 1 }}>
                            Photo {idx + 1} of {photos.concern_photos.length}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                            {filePath}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </React.Fragment>
            );
          })}

          {containers.length === 0 && (
            <Typography variant="body1" align="center" color="text.secondary" sx={{ py: 4 }}>
              No completed containers found for this date.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ '@media print': { display: 'none' } }}>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
        <Button
          onClick={handlePrint}
          variant="contained"
          startIcon={<PrintIcon />}
          sx={{
            backgroundColor: '#CB6015',
            '&:hover': {
              backgroundColor: '#A54E11'
            }
          }}
        >
          Print Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DailyContainerReport;
